import { useEffect, useRef } from "react";
import { Outlet, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { CreateWorkflowProvider, useCreateWorkflowContext } from "@/contexts/CreateWorkflowContext";
import { StepProgressIndicator } from "@/components/shared/StepProgressIndicator";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { trackFunnelEvent } from "@/services/FunnelAnalytics";

const STEP_ROUTES = ["/create/artwork", "/create/support", "/create/order"] as const;

function CreateLayoutInner() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { user } = useCreateWorkflowContext();
  const authGateTrackedRef = useRef(false);
  const isArtworkStep = location.pathname.startsWith("/create/artwork") || location.pathname === "/create";

  // Handle payment return params
  useEffect(() => {
    const paymentStatus = searchParams.get("payment");
    const paymentType = searchParams.get("type");
    const sessionId = searchParams.get("session_id");

    if (paymentStatus === "success") {
      if (paymentType === "credits") {
        if (sessionId) {
          (async () => {
            try {
              const { data, error } = await supabase.functions.invoke('process-credit-payment', {
                body: { sessionId }
              });
              if (error) throw error;
              toast({
                title: "Crédits mis à jour",
                description: data?.alreadyProcessed
                  ? "Ce paiement a déjà été enregistré."
                  : "Vos crédits ont été ajoutés à votre compte.",
              });
            } catch (err) {
              console.error('Credit verification failed:', err);
              toast({
                title: "Vérification du paiement",
                description: "Paiement validé, mise à jour des crédits en cours.",
              });
            } finally {
              navigate('/create/artwork', { replace: true });
            }
          })();
        } else {
          toast({
            title: "Paiement réussi !",
            description: "Vos crédits seront mis à jour sous peu.",
          });
          navigate('/create/artwork', { replace: true });
        }
      } else {
        toast({
          title: "Paiement réussi !",
          description: "Votre commande a été confirmée.",
        });
        navigate('/create/artwork', { replace: true });
      }
    } else if (paymentStatus === "cancelled") {
      toast({
        title: "Paiement annulé",
        description: "Vous pouvez modifier votre commande et réessayer.",
        variant: "destructive",
      });
      const orderId = searchParams.get("order_id");
      const target = orderId ? `/create/order?order_id=${orderId}` : '/create/artwork';
      navigate(target, { replace: true });
    }
  }, [navigate, searchParams, toast]);

  useEffect(() => {
    if (!user && !isArtworkStep && !authGateTrackedRef.current) {
      trackFunnelEvent("auth_gate_viewed", { path: location.pathname });
      authGateTrackedRef.current = true;
    }
  }, [isArtworkStep, location.pathname, user]);

  // Determine current step from route
  const currentPath = location.pathname;
  const currentStepIndex = STEP_ROUTES.findIndex(r => currentPath.startsWith(r));

  const steps = [
    {
      id: "artwork",
      title: "1. L'œuvre",
      description: "Créez votre artwork",
      completed: currentStepIndex > 0,
      current: currentStepIndex === 0,
    },
    {
      id: "support",
      title: "2. Le support",
      description: "Taille et finition",
      completed: currentStepIndex > 1,
      current: currentStepIndex === 1,
    },
    {
      id: "order",
      title: "3. La commande",
      description: "Finalisation",
      completed: false,
      current: currentStepIndex === 2,
    },
  ];

  if (!user && !isArtworkStep) {
    return (
      <div className="min-h-screen pb-12 pt-28 md:pt-32 gradient-hero">
        <div className="container mx-auto px-6">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">Créez votre tableau en quelques minutes</h1>
            <p className="mx-auto mb-6 max-w-2xl text-lg text-foreground/80">
              Un compte gratuit est nécessaire pour sauvegarder vos générations, conserver vos crédits
              et suivre votre commande jusqu'à la livraison.
            </p>
            <div className="mx-auto mb-8 grid max-w-3xl gap-3 text-left text-sm text-foreground/75 sm:grid-cols-3">
              <div className="rounded-2xl bg-white/70 p-4 shadow-sm">✓ Vos essais restent accessibles</div>
              <div className="rounded-2xl bg-white/70 p-4 shadow-sm">✓ Paiement et suivi plus simples</div>
              <div className="rounded-2xl bg-white/70 p-4 shadow-sm">✓ Aucun engagement à l'inscription</div>
            </div>
            <Button
              variant="premium"
              onClick={() => {
                trackFunnelEvent("auth_gate_cta_clicked", { path: location.pathname });
                navigate(`/auth?tab=signup&returnTo=${encodeURIComponent(location.pathname)}`);
              }}
            >
              Créer mon compte gratuit
            </Button>
            <p className="mt-4 text-sm text-foreground/60">
              Déjà client ?{" "}
              <button
                type="button"
                className="font-medium text-primary underline-offset-4 hover:underline"
                onClick={() => navigate(`/auth?tab=login&returnTo=${encodeURIComponent(location.pathname)}`)}
              >
                Connectez-vous pour reprendre votre création
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-12 pt-28 md:pt-32 gradient-hero">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="mb-10 text-center md:mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {user ? "Créez votre " : "Préparez votre "}<span className="text-premium">œuvre d'art</span>
          </h1>
          <p className="text-xl text-foreground/80 max-w-2xl mx-auto mb-6">
            {user
              ? "Transformez votre photo en une œuvre d'art unique grâce à l'intelligence artificielle"
              : "Ajoutez votre photo, choisissez un format et un style, puis créez votre compte gratuit au moment de générer."}
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="mb-12">
          <StepProgressIndicator steps={steps} />
        </div>

        {/* Step Content */}
        <div className="max-w-4xl mx-auto">
          <Outlet />
        </div>
      </div>
    </div>
  );
}

export default function CreateLayout() {
  return (
    <CreateWorkflowProvider>
      <CreateLayoutInner />
    </CreateWorkflowProvider>
  );
}
